// Month-grid calendar of jobs, used as the alt view on the Job Inbox page.
// Owner picks a month, optionally filters by broker / type, and clicks a job
// chip to open the detail modal. The Print button uses CSS media queries so
// the calendar prints cleanly on a single page (no sidebar, no chrome).
//
// Each job chip shows the most useful identifier per direction:
//   direct    → time · rep · "Origin → Destination/OOT"
//   inbound   → time · stops list · ✈
//   outbound  → time · stops list · ✈

import React, { useMemo, useState, useEffect } from "react";

const JOB_TYPE_LABELS = { inbound: "Inbound", outbound: "Outbound", roundtrip: "Round trip", direct: "Direct" };
const TYPE_BG = {
  inbound:   "#E6F1FB",
  outbound:  "#E6F4F0",
  roundtrip: "#f0e8ff",
  direct:    "#FFF4E0",
};
const TYPE_BORDER = {
  inbound:   "#a9c8e0",
  outbound:  "#a9d4c2",
  roundtrip: "#cbb3ea",
  direct:    "#f0c878",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function monthLabel(d) {
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

// Build 6 rows of 7 days starting from the Sunday on or before the 1st.
function buildMonthDays(monthStart) {
  const first = new Date(monthStart);
  const dow = first.getDay(); // 0..6, Sun=0
  const start = new Date(first);
  start.setDate(first.getDate() - dow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function jobRouteLabel(job) {
  const stops = (job.stops || []).filter((s) => s.type !== "airport");
  if (stops.length === 0) return "";
  const labelOf = (s) => s.outOfTown ? "OOT" : (s.locationName || "—");
  if (stops.length === 1) return labelOf(stops[0]);
  return `${labelOf(stops[0])} → ${labelOf(stops[stops.length - 1])}`;
}

export default function JobCalendar({
  jobs,
  onSelectJob,
  // Filter state is shared with the list view so switching tabs preserves
  // selections. Falls back to internal state if the parent doesn't supply them.
  brokerFilter: brokerFilterProp,
  setBrokerFilter: setBrokerFilterProp,
  typeFilter: typeFilterProp,
  setTypeFilter: setTypeFilterProp,
  repFilter: repFilterProp,
  setRepFilter: setRepFilterProp,
  // When the parent's period filter is set ("thisMonth" / "nextMonth"), jump
  // the calendar cursor so the user sees the same month they filtered by.
  periodFilter,
}) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  useEffect(() => {
    if (periodFilter === "thisMonth") setCursor(startOfMonth(new Date()));
    else if (periodFilter === "nextMonth") setCursor(addMonths(startOfMonth(new Date()), 1));
    // periodFilter "all" leaves the cursor wherever the user navigated.
  }, [periodFilter]);
  const [brokerFilterLocal, setBrokerFilterLocal] = useState("all");
  const [typeFilterLocal, setTypeFilterLocal] = useState("all");
  const [repFilterLocal, setRepFilterLocal] = useState("all");
  const brokerFilter = brokerFilterProp ?? brokerFilterLocal;
  const setBrokerFilter = setBrokerFilterProp ?? setBrokerFilterLocal;
  const typeFilter = typeFilterProp ?? typeFilterLocal;
  const setTypeFilter = setTypeFilterProp ?? setTypeFilterLocal;
  const repFilter = repFilterProp ?? repFilterLocal;
  const setRepFilter = setRepFilterProp ?? setRepFilterLocal;

  // Distinct values across the loaded jobs for filter dropdowns.
  const brokers = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.brokerName).filter(Boolean))).sort(),
    [jobs]
  );
  const reps = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.salesRepName).filter(Boolean))).sort(),
    [jobs]
  );

  const filtered = useMemo(() => jobs.filter((j) => {
    if (!j.scheduledDate) return false;
    if (brokerFilter !== "all" && j.brokerName !== brokerFilter) return false;
    if (typeFilter !== "all" && j.jobType !== typeFilter) return false;
    if (repFilter !== "all" && j.salesRepName !== repFilter) return false;
    return true;
  }), [jobs, brokerFilter, typeFilter, repFilter]);

  // Bucket jobs by their scheduledDate (YYYY-MM-DD) for O(1) day-cell lookups.
  const byDate = useMemo(() => {
    const m = new Map();
    filtered.forEach((j) => {
      const k = j.scheduledDate;
      if (!k) return;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(j);
    });
    // Sort each day by scheduledTime so they appear in chronological order.
    m.forEach((list) => {
      list.sort((a, b) => String(a.scheduledTime || "99:99").localeCompare(String(b.scheduledTime || "99:99")));
    });
    return m;
  }, [filtered]);

  const days = buildMonthDays(cursor);
  const monthStart = cursor.getMonth();

  function handlePrint() {
    window.print();
  }

  return (
    <div className="job-calendar">
      {/* Print-only styles: hide non-calendar UI when printing */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .job-calendar, .job-calendar * { visibility: visible; }
          .job-calendar { position: absolute; left: 0; top: 0; width: 100%; }
          .job-calendar .no-print { display: none !important; }
          .job-calendar .calendar-grid { page-break-inside: avoid; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="no-print" style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "10px 14px", background: "#f8f8f8",
        border: "1px solid #e8e8e8", borderRadius: 8, marginBottom: 10,
      }}>
        <button className="btn btn-sm" onClick={() => setCursor((c) => addMonths(c, -1))}>← Prev</button>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111", minWidth: 160, textAlign: "center" }}>
          {monthLabel(cursor)}
        </div>
        <button className="btn btn-sm" onClick={() => setCursor((c) => addMonths(c, 1))}>Next →</button>
        <button className="btn btn-sm" onClick={() => setCursor(startOfMonth(new Date()))}>Today</button>

        <span style={{ width: 1, height: 22, background: "#ddd", margin: "0 4px" }} />

        <label style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 5 }}>
          Broker
          <select value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
            <option value="all">All</option>
            {brokers.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 5 }}>
          Type
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
            <option value="all">All</option>
            <option value="direct">Direct</option>
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
            <option value="roundtrip">Round trip</option>
          </select>
        </label>
        {reps.length > 0 && (
          <label style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 5 }}>
            Rep
            <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
              <option value="all">All</option>
              {reps.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}

        <span style={{ marginLeft: "auto", fontSize: 11, color: "#666" }}>
          {filtered.length} job{filtered.length !== 1 ? "s" : ""} in view
        </span>
        <button className="btn btn-sm btn-blue" onClick={handlePrint} title="Print this month">🖨 Print</button>
      </div>

      {/* Print header — only visible when printing */}
      <div style={{ display: "none", marginBottom: 8, padding: "0 4px" }} className="print-only">
        <div style={{ fontSize: 18, fontWeight: 700 }}>{monthLabel(cursor)} Schedule</div>
        <div style={{ fontSize: 11, color: "#444" }}>
          {brokerFilter !== "all" && <span>Broker: {brokerFilter} · </span>}
          {typeFilter !== "all" && <span>Type: {JOB_TYPE_LABELS[typeFilter]} · </span>}
          {repFilter !== "all" && <span>Rep: {repFilter} · </span>}
          {filtered.length} job{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>
      <style>{`
        @media print { .print-only { display: block !important; } }
      `}</style>

      {/* Day-of-week header */}
      <div style={{
        // minmax(0, 1fr) forces all 7 columns to stay equal even when one cell's
// content (a long location name in a job chip) would otherwise push its
// column wider and squash the rest. Without the `0` min, grid items can
// overflow and visually "merge" cells.
display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 1,
        background: "#e8e8e8", border: "1px solid #e8e8e8", borderBottom: "none",
        borderRadius: "8px 8px 0 0", overflow: "hidden",
      }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} style={{
            padding: "6px 8px", background: "#f8f8f8",
            fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.5,
          }}>{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="calendar-grid" style={{
        // minmax(0, 1fr) forces all 7 columns to stay equal even when one cell's
// content (a long location name in a job chip) would otherwise push its
// column wider and squash the rest. Without the `0` min, grid items can
// overflow and visually "merge" cells.
display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 1,
        background: "#e8e8e8", border: "1px solid #e8e8e8", borderTop: "none",
        borderRadius: "0 0 8px 8px", overflow: "hidden",
      }}>
        {days.map((d, i) => {
          const inMonth = d.getMonth() === monthStart;
          const isToday = ymd(d) === ymd(new Date());
          const list = byDate.get(ymd(d)) || [];
          return (
            <div key={i} style={{
              minHeight: 110, background: inMonth ? "#fff" : "#fafafa",
              padding: "5px 6px", display: "flex", flexDirection: "column", gap: 3,
              opacity: inMonth ? 1 : 0.55,
              minWidth: 0, overflow: "hidden", // pair with minmax(0,1fr) above
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: isToday ? "#fff" : (inMonth ? "#111" : "#aaa"),
                background: isToday ? "#185FA5" : "transparent",
                width: isToday ? 22 : "auto", height: isToday ? 22 : "auto",
                borderRadius: isToday ? "50%" : 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 2,
              }}>
                {d.getDate()}
              </div>
              {list.map((job) => {
                const bg = TYPE_BG[job.jobType] || "#f0f0f0";
                const bd = TYPE_BORDER[job.jobType] || "#ddd";
                const route = jobRouteLabel(job);
                return (
                  <button
                    key={job.id}
                    onClick={() => onSelectJob?.(job.id)}
                    title={`${job.jobNumber || ""} ${job.brokerName || ""}\n${route}`}
                    style={{
                      background: bg, border: `1px solid ${bd}`, borderRadius: 5,
                      padding: "3px 5px", fontSize: 10, color: "#222",
                      textAlign: "left", cursor: "pointer", lineHeight: 1.25,
                      display: "flex", flexDirection: "column", gap: 1,
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 4, fontWeight: 700 }}>
                      <span style={{ color: "#185FA5" }}>{job.scheduledTime || "—"}</span>
                      <span style={{ color: "#666", fontWeight: 500, fontSize: 9, textTransform: "uppercase" }}>
                        {job.jobType?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    {job.salesRepName && (
                      <div style={{ fontWeight: 600, color: "#185FA5" }}>{job.salesRepName}</div>
                    )}
                    {route && (
                      <div style={{
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        color: "#444",
                      }}>{route}</div>
                    )}
                    {!job.salesRepName && job.brokerName && (
                      <div style={{ color: "#666", fontSize: 9 }}>{job.brokerName}</div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
